import { useCallback } from 'react';

import {
  GENERATION_GUIDANCE_TYPE,
  getDefaultGenerationModeLabel,
  getGenerationModeLabel,
} from 'src/data/generation';
import {
  getGroupByTool,
  getToolGenerationModes,
  getToolMixtureSubGenerationTools,
  isMixtureTool,
} from 'src/data/generationTool';
import { getToneOfVoiceForTrackingEvent } from 'src/data/toneOfVoice';
import { createStateSelectorHook } from 'src/lib/sweetState';
import { useBrandVoicesAssets } from 'src/store';

import { LocalStore } from '../store';

const useLocalState = createStateSelectorHook(LocalStore, [
  'selectedModeId',
  'visibleDDTemplates',
  'generationTool',
  'subGenerationToolsProps',
]);

const FORMULA_TYPE = {
  default: 'default',
  preset: 'preset',
  custom: 'custom',
};

const getFormula = ({
  selectedModeId,
  generationTool,
  visibleDDTemplates,
  subGenerationToolsProps,
}) => {
  if (isMixtureTool(generationTool)) {
    return getToolMixtureSubGenerationTools(generationTool).map((generationTool) => {
      const selectedModeId = subGenerationToolsProps[generationTool]['selectedModeId'];
      return {
        generationTool,
        formula: {
          title: getFormulaTitle(selectedModeId, visibleDDTemplates, generationTool),
          formulaType: getFormulaType(selectedModeId, generationTool),
        },
      };
    });
  } else {
    return [
      {
        generationTool,
        formula: {
          title: getFormulaTitle(selectedModeId, visibleDDTemplates, generationTool),
          formulaType: getFormulaType(selectedModeId, generationTool),
        },
      },
    ];
  }
};

const getFormulaType = (selectedModeId, generationTool) => {
  const generationModes = getToolGenerationModes(generationTool);
  // the first generation mode option is the default.
  if (selectedModeId === generationModes[0]) {
    return FORMULA_TYPE.default;
  } else if (generationModes.includes(selectedModeId)) {
    return FORMULA_TYPE.preset;
  }
  return FORMULA_TYPE.custom;
};

export const getFormulaTitle = (selectedModeId, visibleDDTemplates, generationTool) =>
  visibleDDTemplates.indexOf(selectedModeId) === 0
    ? getDefaultGenerationModeLabel(selectedModeId, generationTool)
    : getGenerationModeLabel(selectedModeId, generationTool);

export const useGetDocumentCustomDimensions = () => {
  const { toneOfVoices } = useBrandVoicesAssets();
  const { selectedModeId, visibleDDTemplates, generationTool, subGenerationToolsProps } =
    useLocalState();
  return useCallback(
    (props) => {
      const formula = getFormula({
        selectedModeId,
        generationTool,
        visibleDDTemplates,
        subGenerationToolsProps,
      });

      const category = getGroupByTool(generationTool);
      const url = props?.url;
      const usedHashtags = props?.annotations?.filter(
        (annotation) => annotation.type === GENERATION_GUIDANCE_TYPE.hashtag
      );
      const annotationsOrigins = props?.annotations?.map((a) => a.origin).join(',') || '';
      const tov = getToneOfVoiceForTrackingEvent(props?.tovInput, toneOfVoices);
      const customModel = !!props.customModelId;

      const dimensions = {
        generationTool,
        formula,
        annotationsOrigins,
        hashtags: !!usedHashtags?.length,
        url: !!url,
        ...tov,
        customModel,
      };

      return {
        category,
        ...dimensions,
      };
    },
    [selectedModeId, generationTool, visibleDDTemplates, subGenerationToolsProps, toneOfVoices]
  );
};
